import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AdminLoginGate from "../components/AdminLoginGate";
import AdminStats from "../components/admin/AdminStats";
import RolesImportCard from "../components/admin/RolesImportCard";
import DataExportsCard from "../components/admin/DataExportsCard";
import TopRolesChart from "../components/admin/TopRolesChart";
import {
  getStats,
  getSkills,
  getRoles,
  isAdminAuthed,
  adminLogout,
  adminMe,
  downloadRolesCsv,
  downloadMethodologyMd,
  getMethodologyMd,
  uploadRolesCsv,
} from "../lib/api";
import { toast } from "sonner";

const Admin = () => {
  const [authed, setAuthed] = useState(() => isAdminAuthed());
  const [counts, setCounts] = useState(null);
  const [stats, setStats] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [methodology, setMethodology] = useState("");
  const [showMethodology, setShowMethodology] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      // adminMe verifies the HttpOnly cookie before we fire the data
      // calls; the response interceptor in api.js already clears the
      // local auth hint on any 401, so this double-check is cheap.
      await adminMe();
      const [s, r, st] = await Promise.all([getSkills(), getRoles(), getStats()]);
      setCounts({
        skills: s.count,
        roles: r.count,
        technical: s.technical?.length || 0,
        soft: s.soft?.length || 0,
      });
      setStats(st);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAuthed(false);
        toast.error("Session expired — please re-enter your admin PIN");
      } else {
        toast.error("Couldn't load admin data");
      }
    }
  }, []);

  useEffect(() => {
    if (authed) loadAll();
  }, [authed, loadAll]);

  const handleImport = async (file) => {
    if (!file) return;
    try {
      setImporting(true);
      const data = await uploadRolesCsv(file);
      setImportResult(data);
      toast.success(`Imported ${data?.imported?.roles ?? 0} roles`);
      await loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Import failed";
      toast.error(detail);
    } finally {
      setImporting(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      await downloadRolesCsv();
      toast.success("Downloading roles CSV…");
    } catch {
      toast.error("CSV export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadMethodology = async () => {
    try {
      await downloadMethodologyMd();
      toast.success("Downloading methodology…");
    } catch {
      toast.error("Methodology download failed");
    }
  };

  const handleViewMethodology = async () => {
    if (showMethodology) {
      setShowMethodology(false);
      return;
    }
    try {
      if (!methodology) {
        const md = await getMethodologyMd();
        setMethodology(md);
      }
      setShowMethodology(true);
    } catch {
      toast.error("Couldn't load methodology");
    }
  };

  const handleLogout = async () => {
    await adminLogout();
    toast.success("Signed out");
    window.location.reload();
  };

  if (!authed) {
    return <AdminLoginGate onAuthed={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-700">
              Mattoo internal
            </p>
            <h1
              data-testid="admin-title"
              className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900"
            >
              Admin &middot; Roles &amp; data
            </h1>
          </div>
          <Link
            to="/"
            data-testid="back-to-site-link"
            className="text-sm font-semibold text-slate-700 hover:text-slate-900"
          >
            ← Back to site
          </Link>
          <button
            type="button"
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="ml-3 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>

        <AdminStats counts={counts} stats={stats} />

        <RolesImportCard
          importing={importing}
          importResult={importResult}
          onImport={handleImport}
        />

        <DataExportsCard
          exporting={exporting}
          showMethodology={showMethodology}
          methodology={methodology}
          onExportCsv={handleExportCsv}
          onViewMethodology={handleViewMethodology}
          onDownloadMethodology={handleDownloadMethodology}
        />

        <TopRolesChart topRoles={stats?.top_primary_roles} />
      </main>
      <Footer />
    </div>
  );
};

export default Admin;
